import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import {
  Button as AntdButton,
  Divider,
  Form,
  Input as AntdInput,
  InputNumber,
  Modal,
  Space,
  Typography,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import {
  Beaker,
  Eye,
  History as HistoryIcon,
  MessageSquare,
  RadioTower,
  RotateCw,
  Search as SearchIcon,
  Sparkles,
} from "lucide-react";
import {
  getGenerativeSessions,
  insightGenerativeLogs,
  resetGenerativeSession,
} from "../../../../services/generative-message-logs";
import { formatPhoneNumber } from "../../../../utils";
import View from "./View";
import UserContext from "../../../../context/users";
import { AdminRole } from "../../../../constants";
import { resetEvolutionSession } from "../../../../services/evolution";
import useDebounce from "../../../../hooks/useDebounce";
import { useGenerativeSessionSeed } from "../../../../context/GenerativeSessionContext";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Card } from "../../../../components/ui/card";
import { Avatar } from "../../../../components/ui/avatar";
import { ConfirmDialog } from "../../../../components/ui/dialog";
import { EmptyState } from "../../../../components/ui/empty-state";
import { Pagination } from "../../../../components/ui/pagination";
import { Tooltip } from "../../../../components/ui/tooltip";
import { Spinner } from "../../../../components/ui/spinner";
import { toast } from 'sonner'

export default function Historic({ assistantId }) {
  return (
    <Routes>
      <Route path={"/"} element={<HistoricList assistantId={assistantId} />} />
      <Route path={"/:historic_id"} element={<View assistantId={assistantId} />} />
    </Routes>
  );
}

function HistoricList({ assistantId }) {
  const [historic, setHistoric] = useState([]);
  const [user] = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [insightOpen, setInsightOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { setGenerativeSessionSeedId, loadingSeedId } = useGenerativeSessionSeed();

  const resetEvoSession = useCallback(
    async (id) => {
      await resetEvolutionSession(id)
        .then(() => toast.success("Sessão resetada"))
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao resetar sessão");
        });
    },
    []
  );

  const resetSession = useCallback(
    async (id) => {
      await resetGenerativeSession(id)
        .then(() => toast.success("Sessão resetada"))
        .catch((e) => {
          console.error(e);
          toast.error("Falha ao resetar sessão");
        });
    },
    []
  );

  const getSessions = useCallback(
    async (params) => {
      const { isLiveUpdate, ...searchParams } = params;
      if (!isLiveUpdate) setLoading(true);

      const finalParams = {
        assistantID: assistantId,
        page,
        perPage,
        ...searchParams,
      };

      return getGenerativeSessions(finalParams)
        .then(({ data, total }) => {
          if (!data || data.length === 0) {
            setHistoric([]);
            setTotal(0);
            return;
          }
          const formattedHistoric = data.map((item) => {
            const phone = item.phone ? formatPhoneNumber(item.phone) : null;
            return {
              ...item,
              name: phone || item.sessionId,
              isPhone: !!phone,
            };
          });
          setHistoric(formattedHistoric);
          setTotal(total);
        })
        .catch((err) => {
          toast.error("Erro ao buscar histórico");
          console.error(err);
        })
        .finally(() => {
          if (!isLiveUpdate) setLoading(false);
        });
    },
    [assistantId, page, perPage]
  );

  useEffect(() => {
    getSessions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const [isLive, setIsLive] = useState(() => localStorage.getItem("isLive") === "true");
  const getSessionsRef = useRef(getSessions);
  useEffect(() => {
    getSessionsRef.current = getSessions;
  }, [getSessions]);

  const toggleLiveMode = () => {
    const next = !isLive;
    setIsLive(next);
    localStorage.setItem("isLive", String(next));
  };

  const prevSearchTermRef = useRef();
  useEffect(() => {
    const isNewSearch = prevSearchTermRef.current !== debouncedSearchTerm;
    if (isNewSearch) setPage(1);
    prevSearchTermRef.current = debouncedSearchTerm;
    getSessions({ sessionID: debouncedSearchTerm, page: isNewSearch ? 1 : page });
  }, [debouncedSearchTerm, page, perPage, getSessions]);

  useEffect(() => {
    if (!isLive) return;
    const intervalId = setInterval(() => {
      getSessionsRef.current({
        isLiveUpdate: true,
        sessionID: debouncedSearchTerm,
      });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [isLive, debouncedSearchTerm]);

  const onConfirmReset = useCallback(async () => {
    if (!resetTarget) return;
    setResetting(true);
    const isAdminPhone = user.role === AdminRole && resetTarget.isPhone;
    if (isAdminPhone) {
      await resetEvoSession(resetTarget.sessionId);
    } else {
      await resetSession(resetTarget.sessionId);
    }
    setResetting(false);
    setResetTarget(null);
    getSessions({ sessionID: debouncedSearchTerm });
  }, [resetTarget, user, resetEvoSession, resetSession, getSessions, debouncedSearchTerm]);

  const formatDate = useCallback(
    (date) =>
      new Date(date).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  return (
    <>
      <Insight open={insightOpen} setOpen={setInsightOpen} assistantID={assistantId} />

      <div className="flex flex-col gap-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <HistoryIcon size={18} style={{ color: "var(--dash-primary)" }} />
              <h2
                className="text-xl font-semibold tracking-tight"
                style={{ color: "var(--dash-text)" }}
              >
                Histórico de sessões
              </h2>
            </div>
            <p className="text-xs" style={{ color: "var(--dash-text-muted)" }}>
              Visualize, filtre e analise as conversas do seu assistente.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-72">
              <Input
                leftIcon={SearchIcon}
                placeholder="Session ID ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {user.role === AdminRole && (
              <Tooltip content="Insight da IA">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setInsightOpen(true)}
                >
                  <Sparkles size={15} />
                </Button>
              </Tooltip>
            )}
            <Button
              variant={isLive ? "danger-solid" : "outline"}
              leftIcon={RadioTower}
              onClick={toggleLiveMode}
              className={isLive ? "live-pulse-button" : ""}
            >
              {isLive ? "Ao vivo" : "Iniciar live"}
            </Button>
          </div>
        </header>

        {loading ? (
          <Card className="flex items-center justify-center py-16">
            <Spinner size={20} />
          </Card>
        ) : historic.length === 0 ? (
          <Card>
            <EmptyState
              icon={MessageSquare}
              title="Nenhuma conversa ainda"
              description={
                debouncedSearchTerm
                  ? "Nenhum resultado para a busca atual."
                  : "As conversas dos seus usuários aparecerão aqui assim que forem iniciadas."
              }
            />
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {historic.map((item) => (
              <SessionRow
                key={item.sessionId}
                item={item}
                formatDate={formatDate}
                onReset={() => setResetTarget(item)}
                onUseSession={() => setGenerativeSessionSeedId(item.sessionId)}
                isLoadingSeed={loadingSeedId === item.sessionId}
              />
            ))}
            <Pagination
              page={page}
              pageSize={perPage}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={onConfirmReset}
        title="Resetar sessão?"
        description="Isso irá limpar todo o contexto e histórico da sessão deste usuário. Esta ação não pode ser desfeita."
        confirmLabel="Resetar"
        loading={resetting}
      />
    </>
  );
}

function SessionRow({ item, formatDate, onReset, onUseSession, isLoadingSeed }) {
  return (
    <Card className="flex items-center gap-4 p-4 transition-all hover:border-[var(--dash-primary-border)]">
      <Avatar
        variant={item.isPhone ? "primary" : "default"}
        icon={MessageSquare}
        size="md"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Link
          to={`${item?.sessionId}`}
          className="truncate text-sm font-semibold transition-colors hover:text-[var(--dash-primary)]"
          style={{ color: "var(--dash-text)" }}
        >
          {item?.name}
        </Link>
        <p
          className="truncate text-[11px] tabular-nums"
          style={{ color: "var(--dash-text-muted)" }}
        >
          {formatDate(item?.createdAt)} · {item?.conversations} mensage
          {item?.conversations > 1 ? "ns" : "m"}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <Tooltip content="Resetar sessão">
          <Button variant="ghost" size="icon-sm" onClick={onReset}>
            <RotateCw size={14} style={{ color: "#d61f3f" }} />
          </Button>
        </Tooltip>
        <Tooltip content="Usar no chat de teste">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onUseSession}
            loading={isLoadingSeed}
          >
            <Beaker size={14} />
          </Button>
        </Tooltip>
        <Link to={`${item?.sessionId}`}>
          <Tooltip content="Ver detalhes">
            <Button variant="outline" size="icon-sm">
              <Eye size={14} />
            </Button>
          </Tooltip>
        </Link>
      </div>
    </Card>
  );
}

const Insight = ({ open, setOpen, assistantID }) => {
  const [response, setResponse] = useState();
  const [loading, setLoading] = useState(false);

  const onFinish = useCallback(
    (values) => {
      setLoading(true);
      insightGenerativeLogs({ ...values, assistantID: Number(assistantID) })
        .then(({ data }) => setResponse(data))
        .catch((e) => console.error(e))
        .finally(() => setLoading(false));
    },
    [assistantID]
  );

  return (
    <Modal
      title={"Peça um insight da IA"}
      cancelText={"Fechar"}
      onCancel={() => setOpen(false)}
      footer={<AntdButton onClick={() => setOpen(false)}>Fechar</AntdButton>}
      open={open}
    >
      <br />
      <Form layout={"vertical"} onFinish={onFinish}>
        <Form.Item
          name={"prompt"}
          label={"Mensagem"}
          rules={[{ required: true, min: 10, message: "Um prompt válido tem no mínimo 10 caracteres" }]}
        >
          <AntdInput.TextArea autoSize placeholder={"Existe algum problema?"} />
        </Form.Item>
        <Form.Item
          name={"sessionRecords"}
          label={"Quantidade de conversas"}
          rules={[{ required: true, message: "A quantidade deve estar entre 10 e 300" }]}
        >
          <InputNumber min={10} max={300} />
        </Form.Item>
        <Form.Item>
          <AntdButton
            disabled={loading}
            loading={loading}
            icon={<SendOutlined />}
            htmlType={"submit"}
            type={"primary"}
          >
            Enviar
          </AntdButton>
        </Form.Item>
      </Form>
      {response && (
        <>
          <Divider />
          <Space style={{ width: "100%" }} direction={"vertical"}>
            <Typography.Title level={3}>Insight</Typography.Title>
            <AntdInput.TextArea value={response?.insight} readOnly autoSize={{ minRows: 2, maxRows: 6 }} />
            <Typography.Title level={4}>Exemplos:</Typography.Title>
            {response.bad_examples.map((item, i) => (
              <Link to={item} key={i}>
                <AntdButton type={"link"}>{item}</AntdButton>
              </Link>
            ))}
            <Typography.Title level={3}>Sugestão</Typography.Title>
            <AntdInput.TextArea value={response?.suggestion} readOnly autoSize={{ minRows: 2, maxRows: 6 }} />
            <Typography.Title level={4}>Novo prompt</Typography.Title>
            <AntdInput.TextArea value={response?.updated_prompt} readOnly autoSize={{ minRows: 2, maxRows: 6 }} />
          </Space>
        </>
      )}
    </Modal>
  );
};
